// (c) chaehongee. Confidential and proprietary.

import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { addNode } from '../../../store/slices/sessionSlice'
import { setNodes as setSelectedNodes } from '../../../store/slices/selectionSlice'
import { useMaskActions } from '../../Canvas/MaskContext'
import { uploadMaskNode } from '../utils/maskUtils'
import { useSessionRefresh } from './useSessionRefresh'
import { selectActiveSessionId } from '../../../store/selectors/sessionSelectors'
import { selectSelectedNodeIdsBySessionId } from '../../../store/selectors/selectionSelectors'

interface AttachMaskOptions {
    baseNodeId?: string
}

export interface MaskAttachmentResult {
    baseNodeId: string
    maskNodeId: string
    payloadNodeIds: string[]
}

export function useMaskWorkflow() {
    const dispatch = useAppDispatch()
    const sessionId = useAppSelector(selectActiveSessionId)
    const selectedNodeIds = useAppSelector((s) => (
        sessionId ? selectSelectedNodeIdsBySessionId(s, sessionId) : []
    ))
    const { exportMask } = useMaskActions()
    const refreshSession = useSessionRefresh(sessionId)

    const attachMaskToBase = useCallback(async (options?: AttachMaskOptions): Promise<MaskAttachmentResult | null> => {
        if (!sessionId || !exportMask) {
            return null
        }

        const maskNode = await uploadMaskNode(sessionId, exportMask)
        const resolvedBaseId = options?.baseNodeId ?? selectedNodeIds[selectedNodeIds.length - 1]

        if (!maskNode?.id || !resolvedBaseId) {
            return null
        }

        const payloadNodeIds = [resolvedBaseId, maskNode.id]
        dispatch(addNode({ sessionId, node: maskNode }))
        dispatch(setSelectedNodes({ sessionId, nodeIds: payloadNodeIds }))
        await refreshSession()

        return {
            baseNodeId: resolvedBaseId,
            maskNodeId: maskNode.id,
            payloadNodeIds,
        }
    }, [sessionId, exportMask, selectedNodeIds, dispatch, refreshSession])

    return {
        attachMaskToBase,
    }
}


